import { roundPrice } from 'Util/Price';
import PRODUCT_TYPE from 'Util/Product/Product.config';

import { getPrice } from '../baseProduct.data';

const { configurable, simple, grouped } = PRODUCT_TYPE;

/**
 * Single GA4 `items[]` entry — shared shape between add_to_cart and
 * remove_from_cart, which only differ in event name and (for remove)
 * cart_location.
 *
 * item_id is always the parent/configurable-product SKU; item_variant is
 * only included when a distinct child SKU is actually known (omitted for
 * simple products, where parent and variant are the same thing).
 *
 * @namespace Bodypwa/EventData/Ga4/Product/Data/getAddToCartItem
 */
export const getAddToCartItem = ({
    parentSku, parentName, price, quantity, variantSku
}) => ({
    item_id: parentSku,
    item_name: parentName,
    ...(variantSku && variantSku !== parentSku ? { item_variant: variantSku } : {}),
    price: +roundPrice(price || 0),
    quantity
});

/** @namespace Bodypwa/EventData/Ga4/Product/Data/getAddToCartEventData */
export const getAddToCartEventData = ({
    parentSku, parentName, price, quantity, variantSku, currency
}) => {
    const item = getAddToCartItem({
        parentSku, parentName, price, quantity, variantSku
    });

    return {
        currency,
        value: +roundPrice(item.price * quantity),
        items: [item]
    };
};

/**
 * Builds add_to_cart data from a parent product + the resolved
 * selected/added product (same shape ProductActions/ProductCard pass to
 * <AddToCart product={parent} selectedProduct={resolvedVariantOrParent} />).
 *
 * @namespace Bodypwa/EventData/Ga4/Product/Data/getAddToCartEventDataFromSelection
 */
export const getAddToCartEventDataFromSelection = ({
    product, selectedProduct, quantity, currency
}) => {
    const { sku: parentSku, name: parentName, type_id = simple } = product || {};
    // Prefer the selected variant's own price_range, but fall back to the
    // parent product's if the variant record doesn't carry pricing itself
    // (some variant shapes only carry sku/attributes) — a parent-level
    // price beats silently reporting 0.
    const selectedPrice = selectedProduct
        ? getPrice(selectedProduct, type_id, selectedProduct?.price)
        : 0;
    const price = selectedPrice || getPrice(product, type_id, product?.price);
    const variantSku = selectedProduct?.sku;

    return getAddToCartEventData({
        parentSku, parentName, price, quantity, variantSku, currency
    });
};

/**
 * Resolves {parentSku, parentName, price, variantSku} from a cart line item
 * — shared by add_to_cart (quantity bumped in the cart) and remove_from_cart
 * (quantity decreased or item fully removed), which both fire from the same
 * cart-item shape. Mirrors the price resolution actionProduct.data.js's
 * productQtyChangeData already uses for the legacy event, plus the same
 * parent-price fallback as getAddToCartEventDataFromSelection — don't
 * report 0 just because the variant record lacks its own pricing.
 *
 * @namespace Bodypwa/EventData/Ga4/Product/Data/resolveCartLineItemForGa4
 */
const resolveCartLineItemForGa4 = (item) => {
    const {
        sku: variantSku, price, product = {}
    } = item || {};
    const { sku: parentSku, name: parentName, type_id = simple, variants } = product;

    const priceSource = type_id === configurable ? variants?.[0] : product;
    const resolvedPrice = (priceSource && getPrice(priceSource, type_id, price))
        || getPrice(product, type_id, price);

    return {
        parentSku, parentName, price: resolvedPrice, variantSku
    };
};

/**
 * Builds add_to_cart data from a cart line item (the shape used when a
 * quantity is bumped directly in the cart, e.g. Cart.dispatcher's
 * changeItemQty).
 *
 * @namespace Bodypwa/EventData/Ga4/Product/Data/getAddToCartEventDataFromCartItem
 */
export const getAddToCartEventDataFromCartItem = ({ item, quantity, currency }) => {
    const {
        parentSku, parentName, price, variantSku
    } = resolveCartLineItemForGa4(item);

    return getAddToCartEventData({
        parentSku, parentName, price, quantity, variantSku, currency
    });
};

/**
 * Builds remove_from_cart data from a cart line item — used both for a
 * partial quantity decrease (Cart.dispatcher's changeItemQty) and a full
 * item removal (CartItemActions' handleRemoveItem/handleMoveToWishlist).
 *
 * @namespace Bodypwa/EventData/Ga4/Product/Data/getRemoveFromCartEventData
 */
export const getRemoveFromCartEventData = ({
    item, quantity, cartLocation, currency
}) => {
    const {
        parentSku, parentName, price, variantSku
    } = resolveCartLineItemForGa4(item);
    const lineItem = getAddToCartItem({
        parentSku, parentName, price, quantity, variantSku
    });

    return {
        currency,
        value: +roundPrice(lineItem.price * quantity),
        cart_location: cartLocation,
        items: [lineItem]
    };
};

/**
 * Builds add_to_cart data for a wishlist "add to cart", resolving the
 * chosen variant via configurableVariantIndex the same way
 * WishlistItem.container's addItemToCart already does. Returns null for
 * grouped products — a bundle of independently-quantified simple products
 * has no single meaningful price/SKU/quantity to report as one GA4 item,
 * and the legacy event never resolved one either (it fired with a zeroed
 * price for GROUPED type_id).
 *
 * @namespace Bodypwa/EventData/Ga4/Product/Data/getAddToCartEventDataFromWishlistItem
 */
export const getAddToCartEventDataFromWishlistItem = ({
    item, configurableVariantIndex, quantity, currency
}) => {
    const {
        sku: parentSku, name: parentName, type_id = simple, variants
    } = item || {};

    if (type_id === grouped) {
        return null;
    }

    const selectedVariant = type_id === configurable && configurableVariantIndex >= 0
        ? variants?.[configurableVariantIndex]
        : null;
    // Same parent-price fallback as getAddToCartEventDataFromSelection —
    // don't report 0 just because the variant record lacks its own pricing.
    const selectedPrice = selectedVariant
        ? getPrice(selectedVariant, type_id, selectedVariant?.price)
        : 0;
    const price = selectedPrice || getPrice(item, type_id, item?.price);
    const variantSku = selectedVariant?.sku;

    return getAddToCartEventData({
        parentSku, parentName, price, quantity, variantSku, currency
    });
};
